import { type IMoney, MoneyModel } from '@/services/models/Money';
import { PriceModel } from '@/services/models/Price';
import { useMemo } from 'react';
import type { ILineItemSummary } from '..';

/**
 * Describes the pricing information for a line item and which of the
 * strikethroughs to display.
 */
export interface ILineItemPricing {
  /** The current total price of the line item per item. */
  perItemCurrentTotal: IMoney;
  /**
   * The original total price of the line item per item,
   * which can be undefined if the item is not on sale.
   * */
  perItemOriginalTotal: IMoney | undefined;
  /** Whether to show the strikethrough on the each column. */
  showEachStrikethrough: boolean;
  /** Whether to show the strikethrough on the total column. */
  showTotalStrikethrough: boolean;
}

/**
 * Hook used to obtain the pricing information for a line item and also which of the
 * strikethroughs to display.
 * @param line - The line item summary to get the pricing information for.
 * @returns - The pricing information for the line item.
 */
export const useLineItemPricing = ({
  unitPrice,
  subtotal,
  netTotal,
  quantity
}: ILineItemSummary): ILineItemPricing =>
  useMemo(() => {
    const priceModel = PriceModel.from(
      'price' in unitPrice ? unitPrice : { price: unitPrice }
    );

    // If the item is on sale, we want to display the sale and original
    // price of the item as well as the strikethroughs for both.
    if (priceModel.isOnSale) {
      return {
        perItemCurrentTotal: MoneyModel.from(priceModel.price),
        // Safe to use ! because we know the item is on sale.
        perItemOriginalTotal: MoneyModel.from(priceModel.originalPrice!),
        showEachStrikethrough: true,
        showTotalStrikethrough: true
      };
    }
    /** If the subtotal is different from the net total, there is a discount applied. */
    const hasLineDiscount = MoneyModel.compare(subtotal, netTotal) !== 0;

    if (hasLineDiscount) {
      return {
        perItemCurrentTotal: MoneyModel.from(priceModel.price),
        perItemOriginalTotal: undefined,
        showEachStrikethrough: false,
        showTotalStrikethrough: true
      };
    }

    // If the item is not on sale and there is no discount applied,
    // we don't display any strikethroughs.
    return {
      perItemCurrentTotal: MoneyModel.divide(netTotal, quantity),
      perItemOriginalTotal: MoneyModel.divide(subtotal, quantity),
      showEachStrikethrough: false,
      showTotalStrikethrough: false
    };
  }, [netTotal, subtotal, quantity, unitPrice]);
